import { useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { AppText, Button, IconBox } from '@zippeditoolsjs/ui-web';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import BackLink from '../../ui/controls/BackLink';
import AddSecondFactorOptIn from './AddSecondFactorOptIn';
import OtpCodeInput from '../../utils/OtpCodeInput';
import { getTotpHint } from '../policy/totpChallenge';
import { maskPhoneNumber } from '../../utils/phone';
import { getSafeErrorIdentifier } from '../../utils/getSafeErrorIdentifier';

export default function OPTCode(props) {
  const otpRef = useRef(null);
  const { t } = useTranslation();
  const { setIsLoading, offerFactor, optInChecked, onToggleOptIn } = props;
  const code = props.code ? props.code : '';
  const resolver = props.resolver ? props.resolver : null;
  const PhoneNumber = props.PhoneNumber ? props.PhoneNumber : '';
  const showOptIn = Boolean(offerFactor) && Boolean(onToggleOptIn);
  const locked = Boolean(props.sendingOPT) || Boolean(props.busy);

  const handleChangeCode = newValue => {
    // No auto-submit at six digits: verification needs an explicit press, so a
    // mistyped digit cannot burn the code on its own.
    props.setCaptchaCode(newValue);
  };

  const { resetChallengeRecaptcha } = props;
  // Tells a real departure from StrictMode's synthetic one: only the latter is
  // followed by a mount that still sees this set, and must not resend.
  const cleanupPendingRef = useRef(false);

  useEffect(() => {
    if (cleanupPendingRef.current) {
      cleanupPendingRef.current = false;
    } else {
      async function asyncSendFirstSMS() {
        setIsLoading(false);
        await props.sendFirstSMS(props.resolver);
      }
      asyncSendFirstSMS();
    }
    // The verifiers are anchored to this screen's DOM (#first-sms and
    // #recaptcha-button-resend), so a real departure has to tear them down.
    return () => {
      cleanupPendingRef.current = true;
      // Deferred so the synchronous StrictMode remount can cancel it first.
      queueMicrotask(() => {
        if (!cleanupPendingRef.current) return;
        cleanupPendingRef.current = false;
        resetChallengeRecaptcha && resetChallengeRecaptcha();
      });
    };
  }, []);

  const sendVerificationCode = async newValue => {
    try {
      if (resolver != null && !props.showCode) {
        props.setUpRecaptha();
      } else {
        props.verifySMSCode(newValue);
      }
    } catch (error) {
      console.error(
        'OPTCode.sendVerificationCode:',
        getSafeErrorIdentifier(error),
      );
    }
  };

  const restartTimer = () => {
    props.setCaptchaCode('');
    props.setShowButton(false);
    props.setCountdown(15);
    // The verifier no longer hangs off this button, so nothing runs grecaptcha
    // on the click but this.
    props.triggerResend();
  };

  useEffect(() => {
    async function asyncLogInvalidation() {
      if (props.finishedSMS === true) {
        await props.logInValidation(props.resolver);
      }
    }
    asyncLogInvalidation();
  }, [props.finishedSMS]);

  useEffect(() => {
    // Clearing the code upstream (e.g. after a wrong code) resets the input.
    if (code === '' && otpRef.current) {
      otpRef.current.reset();
    }
  }, [code]);

  return (
    <div className="flex flex-col">
      {!props.isLogin && props.onBackToPhone ? (
        <BackLink onClick={props.onBackToPhone} disabled={locked} />
      ) : null}
      <IconBox className="mb-4">
        <IconComponent iconName="chatbubble-ellipses" style={{ fontSize: 22 }} />
      </IconBox>

      <AppText variant="titleXLarge" as="h3" className="mb-2">
        {t('heimdal_app.OTP.sms_code_title')}
      </AppText>
      <AppText variant="bodyMedium" as="p" className="mb-6 text-secondary">
        <Trans
          i18nKey="heimdal_app.OTP.sms_code_subtitle"
          values={{ phone: maskPhoneNumber(PhoneNumber) }}
          components={{ strong: <strong className="font-semibold text-primary" /> }}
        />
      </AppText>

      <div className="mb-[18px]">
        <OtpCodeInput
          ref={otpRef}
          value={code}
          label={t('heimdal_app.OTP.sms_code_title')}
          disabled={locked}
          onChange={handleChangeCode}
        />
      </div>

      <div className="mb-[22px] flex items-center gap-1.5">
        <span className="font-sans text-[13px] text-muted">
          {t('heimdal_app.OTP.not_received')}
        </span>
        <button
          type="button"
          onClick={restartTimer}
          disabled={!props.showButton || locked}
          // During the cooldown it drops the accent and the underline, so the
          // countdown does not read as a live link.
          className={`border-0 bg-transparent p-0 font-sans text-[13px] font-semibold disabled:cursor-not-allowed ${
            props.showButton
              ? 'cursor-pointer text-accent hover:underline disabled:opacity-60'
              : 'text-muted'
          }`}>
          {props.showButton
            ? t('heimdal_app.OTP.resend')
            : `${t('heimdal_app.OTP.resend')} (${props.countdown}s)`}
        </button>
      </div>

      {showOptIn ? (
        <AddSecondFactorOptIn
          factor={offerFactor}
          checked={Boolean(optInChecked)}
          onToggle={onToggleOptIn}
          disabled={locked}
        />
      ) : null}

      <Button
        fullWidth
        loading={props.sendingOPT}
        disabled={code.length < 6 || locked}
        onClick={() => sendVerificationCode(code)}>
        {showOptIn && optInChecked
          ? t('heimdal_app.TOTP.verify_and_add')
          : t('heimdal_app.OTP.verify')}
      </Button>

      {props.isLogin && resolver && getTotpHint(resolver) && props.onUseAnotherMethod ? (
        <button
          type="button"
          onClick={props.onUseAnotherMethod}
          // Locked while a verification is in flight: leaving mid-request tears
          // this screen's verifiers down under the pending call.
          disabled={locked}
          className="mt-3.5 flex cursor-pointer items-center justify-center gap-1.5 border-0 bg-transparent p-1 font-sans text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-60">
          {t('heimdal_app.OTP.use_another_method')}
        </button>
      ) : null}

      {/* Both anchors stay mounted and empty for the life of the screen, and
          React never renders anything into them. The resend verifier used to
          hang off the button above, whose label, classes and disabled state all
          change on every countdown tick: React writing those back wipes the DOM
          grecaptcha injected there, and restoring it afterwards threw reading
          `.style` — surfacing on iOS as an opaque "Script error.". */}
      <div id="recaptcha-button-resend" />
      {/* Stays mounted for the life of the screen: the first-sms verifier is
          destroyed through grecaptcha's own API once the send settles, so this
          node no longer has to be pulled out to free the container. */}
      <div id="first-sms" />
    </div>
  );
}
